/**
 * @fileoverview O GATE do `useCatalogRefresh` — extraído do `providers.tsx`
 * porque foi exatamente aqui que o catálogo sumiu.
 *
 * `useCatalogRefresh` é o ÚNICO caminho automático de (re)carga do snapshot no
 * app (o outro caller é o botão manual de Configurações). Com `providerKey`
 * null ele não agenda NADA: sem tick de 5s, sem poll de 30min, sem refresh ao
 * voltar do standby. Logo, este predicado é o interruptor geral do catálogo, e
 * a polaridade do DEFAULT dele é o que decide se o app falha aberto ou fechado.
 *
 * Histórico (a regressão que este arquivo tranca):
 *   main    → `if (!isAuthed || showProbe) return null`
 *             `showProbe` nasce `false` (useState) = gate ABERTO por padrão.
 *   develop → `if (!isAuthed || !bootSnapshotDone) return null`
 *             `phase` nasce `'idle'` (≠ `'done'`) = gate FECHADO por padrão,
 *             e QUALQUER fase não-terminal travava o catálogo pra sempre.
 *
 * O `boot-snapshot.ts` já documentava a armadilha na doc do
 * `getBootSnapshotInFlight` ("`idle` também não é `done` (...) quem guardasse UI
 * em `!done` deixaria essa sessão presa pra sempre no estado transitório").
 * A `HomeScreen` obedeceu; o `providers.tsx` não. A diferença entre os dois era
 * uma linha ANÔNIMA dentro de um componente de 400 linhas, invisível pro
 * typecheck e sem teste possível sem montar a árvore inteira do app.
 *
 * Por isso o gate virou uma UNIDADE NOMEADA: o teste
 * (`boot-gate-terminacao.test.tsx`) exercita ESTA função, não uma transcrição
 * dela. Gate copiado pro teste é PROXY — ele prova que a cópia está certa, que
 * é precisamente o que não estava em questão.
 *
 * A pergunta certa é AFIRMATIVA: "tem download de BOOT em voo?". Ela responde
 * `false` tanto no fim quanto no caso "nunca teve" — que é a diferença entre
 * um gate e um bloqueio.
 */

import { useMemo } from 'react'

import { useBootSnapshotInFlight } from '../boot-snapshot'

export interface CatalogRefreshGateInput {
  isAuthed: boolean
  providerCode: string | null
  /** Nome da lista no modo direto (sem provider). */
  directListName: string | null
}

/**
 * Predicado PURO do gate, separado do hook pra poder ser exercitado sem React.
 *
 * ⚠️ A única condição de bloqueio é `bootInFlight` — um estado com TERMINAÇÃO
 * GARANTIDA (o `run()` do `boot-snapshot` tem prazo duro). Nunca troque isto por
 * "ainda não terminou com sucesso": estado sem terminação garantida não pode
 * segurar carga de dados.
 */
export function catalogRefreshProviderKey(
  input: CatalogRefreshGateInput & { bootInFlight: boolean },
): string | null {
  const { isAuthed, bootInFlight, providerCode, directListName } = input
  if (!isAuthed || bootInFlight) return null
  return providerCode || (directListName ? `direct:${directListName}` : null)
}

/**
 * Chave de provider pro `useCatalogRefresh`, ou `null` enquanto o fetch de BOOT
 * do snapshot está em voo (evita 2 downloads concorrentes do MESMO snapshot no
 * boot — a intenção original do #553, preservada).
 */
export function useCatalogRefreshProviderKey({
  isAuthed,
  providerCode,
  directListName,
}: CatalogRefreshGateInput): string | null {
  const bootInFlight = useBootSnapshotInFlight()
  return useMemo(
    () => catalogRefreshProviderKey({ isAuthed, bootInFlight, providerCode, directListName }),
    [isAuthed, bootInFlight, providerCode, directListName],
  )
}
